import { useEffect, useRef } from "react";
import { trackCampaign } from "@/lib/campaignTrack";
import { markSeen } from "@/lib/campaignFrequency";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Full-width marketing banner rendered inside the product grid. Fires a GA
 * `campaign_impression` once it's actually scrolled into view, and a
 * `campaign_click` when tapped (which opens the Share-a-Fit form).
 */
const InlineBanner = ({ campaign, onShareClick }) => {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !seen.current) {
            seen.current = true;
            // Count the "how often" cap only once the banner is actually seen,
            // mirroring the popup — an off-screen banner isn't spent.
            markSeen(campaign);
            trackCampaign("campaign_impression", campaign, { placement: "inline" });
            obs.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [campaign]);

  const handleClick = () => {
    trackCampaign("campaign_click", campaign, { placement: "inline" });
    onShareClick?.();
  };

  return (
    <div
      ref={ref}
      className="col-span-full my-2 md:my-4"
      data-testid={`inline-campaign-${campaign.id}`}
    >
      <button
        type="button"
        onClick={handleClick}
        className="block w-full rounded-md overflow-hidden border border-[#E4E1D9] hover:opacity-95 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#2D302A]"
        aria-label="Share your fit"
      >
        <img
          src={`${BACKEND_URL}${campaign.image_url}`}
          alt={campaign.title || "Featured"}
          className="w-full h-auto block"
        />
      </button>
    </div>
  );
};

export default InlineBanner;
